import { LinkEntity } from "@/indexedDB/types";
import { ExternalLink } from "lucide-react";
import { formatTimeAgo } from "@/utils/dates";
import { useAttachmentViewerState } from "@/zustand/AttachmentViewerState";
import { Button } from "@/components/primitives/button";
import { useState } from "react";

/**
 * Individual link component
 * Displays a single link with its title, URL, and timestamp in the right column's links tab
 *
 * How it works:
 * 1. Extracts link metadata from the LinkEntity (title, description, URL, timestamp)
 * 2. Renders a preview image if ogImage is available, falls back to an icon
 * 3. Displays link information with title, description, and timestamp
 * 4. Handles click events to open the attachment viewer
 * 5. Provides an external link button to open the URL in a new tab
 *
 * Key features:
 * - Image preview with fallback to icon
 * - Error handling for failed image loads
 * - Truncated text with line clamping
 * - Consistent layout with other attachment types
 * - External link button for direct URL access
 */
interface LinkProps {
  link: LinkEntity;
}

export default function Link({ link }: LinkProps) {
  const {
    id,
    chatId,
    url,
    metaTitle,
    metaDescription,
    domainUrl,
    timestamp,
    ogImage,
  } = link;

  // State for image error tracking
  const [imageError, setImageError] = useState(false);

  // Get the openDialog function from the attachment viewer state
  const openDialog = useAttachmentViewerState((state) => state.openDialog);

  // Handle click to open attachment viewer
  function handleAttachmentClick(e: React.MouseEvent) {
    // Prevent default to avoid navigating away when clicking
    e.preventDefault();

    openDialog({
      id,
      messageId: id, // Use id as messageId since LinkEntity doesn't have messageId
      chatId,
      timestamp,
      filename: metaTitle || url,
      fileType: "text/html",
    });
  }

  // Format the time ago (e.g., "2 hours ago")
  const timeAgo = formatTimeAgo(timestamp);

  // Extract domain from URL for display
  const domain =
    domainUrl ||
    (() => {
      try {
        const urlObj = new URL(url);
        return urlObj.hostname;
      } catch {
        return url;
      }
    })();

  return (
    <div className="flex items-center gap-2">
      {/* Link preview image or fallback icon */}
      <div
        className="relative flex size-24 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-sm bg-muted"
        onClick={handleAttachmentClick}
      >
        {ogImage && !imageError ? (
          <img
            src={ogImage}
            alt={metaTitle || url}
            className="size-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <ExternalLink className="size-5 text-muted-foreground" />
        )}
      </div>

      {/* Link information - clickable to open viewer */}
      <div
        className="text-md flex w-full min-w-0 flex-1 cursor-pointer flex-col overflow-hidden"
        onClick={handleAttachmentClick}
      >
        <span className="text-m line-clamp-2 font-medium">
          {metaTitle || url}
        </span>
        <span className="line-clamp-2 text-xs text-muted-foreground">
          {metaDescription}
        </span>
        <span className="line-clamp-1 text-xs text-muted-foreground">
          {domain}
        </span>
        <span className="text-xs text-muted-2">{timeAgo}</span>
      </div>

      {/* External link button with fixed position */}
      <Button
        onClick={(e) => {
          e.stopPropagation();
          window.open(url, "_blank", "noopener,noreferrer");
        }}
        size="icon"
        variant="ghost"
        className="ml-2 shrink-0 text-secondary opacity-70"
      >
        <ExternalLink size={16} />
      </Button>
    </div>
  );
}
